import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { MapPin, Users, ArrowRight, Building2, FileText, ChevronDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DocumentPanel from "@/components/app/DocumentPanel";

const SURFACES = { grass: "Grass", "3g": "3G All-Weather", indoor: "Indoor", astro: "Astro" };

export default function Pitches() {
  const [venues, setVenues] = useState([]);
  const [venueId, setVenueId] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/venues");
      setVenues(data);
      setLoading(false);
    })();
  }, []);

  const visibleVenues = useMemo(() => {
    const withActive = venues
      .map(v => ({ ...v, pitches: (v.pitches || []).filter(p => p.active) }))
      .filter(v => v.pitches.length > 0);
    if (venueId === "all") return withActive;
    return withActive.filter(v => v.id === venueId);
  }, [venues, venueId]);

  const totalPitches = visibleVenues.reduce((n, v) => n + v.pitches.length, 0);

  // Jump straight to a pitch card by scrolling it into view + briefly ringing it green.
  const jumpToPitch = (pitchId) => {
    if (!pitchId) return;
    const el = document.querySelector(`[data-testid="pitch-card-${pitchId}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-2", "ring-[#83B68B]", "ring-offset-2", "ring-offset-background");
    setTimeout(() => el.classList.remove("ring-2", "ring-[#83B68B]", "ring-offset-2", "ring-offset-background"), 2000);
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Available spaces</div>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-2 leading-tight">Venues &amp; Pitches</h1>
          <p className="text-sm opacity-60 mt-2">{totalPitches} pitch{totalPitches === 1 ? "" : "es"} across {visibleVenues.length} venue{visibleVenues.length === 1 ? "" : "s"}</p>
        </div>
        <div className="flex flex-col md:flex-row md:items-end gap-4 w-full md:w-auto">
          <div className="w-full md:w-64">
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-2">Venue</div>
            <Select value={venueId} onValueChange={setVenueId}>
              <SelectTrigger data-testid="pitches-venue-select">
                <SelectValue placeholder="All venues" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All venues</SelectItem>
                {venues.map(v => {
                  const count = (v.pitches || []).filter(p => p.active).length;
                  return <SelectItem key={v.id} value={v.id}>{v.name} ({count})</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
          <div className="w-full md:w-72">
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-2">Jump to pitch</div>
            <Select value="" onValueChange={jumpToPitch}>
              <SelectTrigger data-testid="pitches-jump-to-pitch">
                <SelectValue placeholder="Pick a pitch…" />
              </SelectTrigger>
              <SelectContent className="max-h-[400px]">
                {visibleVenues.map(v => (
                  <div key={v.id}>
                    <div className="px-2 py-1 text-[10px] tracking-widest uppercase opacity-60">{v.name}</div>
                    {v.pitches.map(p => (
                      <SelectItem key={p.id} value={p.id} data-testid={`pitches-jump-to-pitch-${p.id}`}>{p.name}</SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="opacity-60 font-display tracking-widest">LOADING PITCHES…</div>
      ) : visibleVenues.length === 0 ? (
        <div className="card-tactical rounded-md p-10 text-center opacity-70">
          No active pitches to show. Add pitches from the Admin panel.
        </div>
      ) : (
        <div className="space-y-12" data-testid="venues-list">
          {visibleVenues.map(v => (
            <section key={v.id} data-testid={`venue-block-${v.id}`}>
              <div className="flex items-baseline justify-between mb-5 pb-3 border-b border-white/10 dark:border-white/10 border-black/10">
                <div>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 opacity-60" />
                    <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight">{v.name}</h2>
                  </div>
                  {v.address && <div className="text-xs opacity-60 mt-1 pl-6">{v.address}</div>}
                </div>
                <div className="text-xs opacity-50 uppercase tracking-widest">{v.pitches.length} pitch{v.pitches.length === 1 ? "" : "es"}</div>
              </div>

              <VenueDocumentsAccordion venue={v} />


              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {v.pitches.map(p => (
                  <Link
                    to={`/app/pitches/${p.id}`}
                    key={p.id}
                    data-testid={`pitch-card-${p.id}`}
                    className="card-tactical rounded-md overflow-hidden group"
                  >
                    <div className="relative h-40 overflow-hidden">
                      {p.image_url ? (
                        <img src={p.image_url} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      ) : (
                        <div className="w-full h-full bg-[#111827] dark:bg-[#111827]" style={{ background: "hsl(var(--muted))" }} />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      <div className="absolute top-3 left-3 px-2 py-1 bg-[#83B68B] text-white text-[10px] font-bold tracking-widest uppercase rounded">
                        {SURFACES[p.surface] || p.surface}
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-display font-black text-xl tracking-tight">{p.name}</h3>
                      <div className="mt-3 flex items-center gap-4 text-xs opacity-60">
                        <span className="inline-flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {p.location}</span>
                        {p.capacity ? <span className="inline-flex items-center gap-1.5"><Users className="w-3 h-3" /> {p.capacity}</span> : null}
                      </div>
                      {p.description && <p className="mt-3 text-sm opacity-60 line-clamp-2">{p.description}</p>}
                      <div className="mt-4 flex items-center justify-end">
                        <div className="inline-flex items-center gap-2 text-sm opacity-70 group-hover:text-[#83B68B] transition-colors">
                          Book <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Lazy-loaded per-venue documents strip. Coaches (and members) can expand any
 * venue to browse + download the paperwork admins have attached — ground safety
 * certs, hire agreements, fixture arrangements, etc. Uses the same read-only
 * `DocumentPanel` shown on the club library so the download UX is identical.
 * Fetches happen on first expand only, so initial page load stays cheap.
 */
function VenueDocumentsAccordion({ venue }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-5" data-testid={`venue-docs-${venue.id}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid={`venue-docs-toggle-${venue.id}`}
        className="w-full flex items-center justify-between gap-3 px-4 py-2.5 rounded border border-border bg-muted/40 hover:border-[#83B68B]/50 transition-colors text-left"
      >
        <div className="flex items-center gap-2 text-xs tracking-widest uppercase opacity-80">
          <FileText className="w-3.5 h-3.5 text-[#83B68B]" />
          <span>Venue documents</span>
          <span className="opacity-50">· ground rules, hire agreements, safety</span>
        </div>
        <ChevronDown className={`w-4 h-4 opacity-60 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3">
          <DocumentPanel
            listEndpoint={`/files/document/venue/${venue.id}`}
            uploadEndpoint={`/files/document/venue/${venue.id}`}
            canEdit={false}
            emptyLabel={`No documents on file for ${venue.name} yet — ask an admin to attach ground rules or a hire agreement.`}
            testIdPrefix={`venue-docs-panel-${venue.id}`}
          />
        </div>
      )}
    </div>
  );
}
